// @flow

import * as React from 'react';
import { Link } from '@ta/common.routing';
import { ClickTracker } from '@ta/common.tracking';
import { PlaceTypeIcon } from '@ta/social.place-type';
import SearchRecordTracker from '@ta/search.tracking';
import type { SearchRecordData } from '@ta/search.tracking';
import { globalize, localize } from '@ta/common.i18n';
import classnames from '@ta/common.classnames';
import styles from './SearchResultSection.less';

type SearchResult = {|
  url: string,
  parent?: string,
  locationType: ?string,
  searchTrackingData?: SearchRecordData,
  filterCount?: number,
  fromDate?: string,
  toDate?: string,
  searchDate?: string,
  route?: any,
|};

type Props = {|
  title: string,
  result: SearchResult,
  shelfKey: string,
|};

const getPlacetypeFromLocationType = (locationType: ?string) => {
  switch (locationType) {
    case 'HOTEL':
      return 'ACCOMMODATION';
    case 'ATTRACTION':
      return 'ATTRACTION';
    case 'RESTAURANT':
      return 'EATERY';
    case 'ATTRACTION_PRODUCT':
      return 'ACTIVITY';
    case 'AIRPORT':
      return 'AIRLINE';
    case 'GEO':
      return 'CITY';
    default:
      return null;
  }
};

const SearchResultSection = (props: Props) => {
  const { result, title, shelfKey } = props;
  const {
    url,
    locationType,
    searchTrackingData,
    route,
    searchDate,
    toDate,
    fromDate,
    filterCount,
    parent,
  } = result;

  const getLocalDateFromString = (selectedDate: string) => {
    const splitDate = selectedDate.split('-');
    // Note: Month value is offset by -1 because JS Dates use zero-indexed month values
    return new Date(
      parseInt(splitDate[0], 10),
      parseInt(splitDate[1], 10) - 1,
      parseInt(splitDate[2], 10),
    );
  };

  let searchDateString;
  if (searchDate) {
    const timeZoneOffset = new Date().getTimezoneOffset(); // minutes of timezone offset
    const todayDateMidnight = new Date().setHours(0, -1 * timeZoneOffset, 0, 0); // Get the start of today UTC
    const searchDateMidnight = new Date(searchDate); // From date UTC
    const numDaysDiff = Math.round(
      (todayDateMidnight - searchDateMidnight) / (1000 * 24 * 60 * 60),
    ); // Subtract the two to get the integer number of days ago

    if (numDaysDiff === 0) {
      searchDateString = localize('social_category_search_today'); // Today
    } else if (numDaysDiff === 1) {
      searchDateString = localize('social_category_search_yesterday'); // Yesterday
    } else if (numDaysDiff < 7) {
      searchDateString = localize('social_category_search_days_ago', { nDays: numDaysDiff }); // n days ago
    } else {
      const numWeeksAgo = Math.round(numDaysDiff / 7);
      searchDateString = localize('social_category_search_weeks_ago', { '0': numWeeksAgo }); // n week(s) ago
    }
  }

  let searchDateParamString;
  if (toDate && fromDate) {
    searchDateParamString = localize('date_range_medium_month_to_day_of_month', {
      sDateStart: globalize.formatDate('date_medium_month', getLocalDateFromString(fromDate)),
      nDayEnd: globalize.formatDate('date_medium_month', getLocalDateFromString(toDate)),
    });
  }

  const iconType = getPlacetypeFromLocationType(locationType);

  return (
    <SearchRecordTracker searchRecordData={searchTrackingData}>
      <ClickTracker
        module="Search_Results"
        action="Search_Results_Selection"
        context={`${shelfKey} | card`}
        value={searchTrackingData?.ordinal}
        sectionNumber={searchTrackingData?.sectionNumber}
        once={false}
        useSpan
      >
        <Link href={url || (route && route.url)}>
          <div className={classnames(styles.section, 'search-result-section')}>
            <div className={styles.inner}>
              <div>
                {searchDateString ? (
                  <div className={styles.search_date}> {searchDateString}</div>
                ) : null}
                <div className={classnames(styles.title, 'title')}>{title}</div>
                {parent ? (
                  <div className={classnames(styles.parent, 'parent')}>{parent}</div>
                ) : null}
                {searchDateParamString ? (
                  <div className={styles.search_date}> {searchDateParamString} </div>
                ) : null}
                {filterCount ? (
                  <div className={styles.search_date}>
                    {' '}
                    {localize('social_category_search_num_filters', { '0': filterCount })}{' '}
                  </div>
                ) : null}
              </div>
              {iconType && <PlaceTypeIcon placeType={iconType} className={styles.ui_icon} />}
            </div>
          </div>
        </Link>
      </ClickTracker>
    </SearchRecordTracker>
  );
};

export default SearchResultSection;
